import { Shot, ShotVersion, SubtitleEraseTask } from '../../../types';

export function isVersionReady(version: ShotVersion) {
  return version.taskStatus === 'completed' && !!version.videoURL;
}

const ERASED_PREFIX = 'erased:';

/** AI剪辑发起门槛：每个分镜至少有一个已完成且有视频的版本；
 *  显式 pin 了主版本的分镜，其 pin 版本必须可用（与后端门槛对齐）。
 *  `erased:` 前缀按 getSelectedVersionId 的既有语义剥掉后指向原版本。 */
export function isEpisodeClipReady(
  shots: Shot[],
  shotVersions: Record<string, ShotVersion[]>,
): boolean {
  if (shots.length === 0) return false;
  return shots.every(shot => {
    const versions = shotVersions[shot.id] || [];
    if (!versions.some(isVersionReady)) return false;
    if (shot.selectedVersionId) {
      const pinnedId = shot.selectedVersionId.startsWith(ERASED_PREFIX)
        ? shot.selectedVersionId.slice(ERASED_PREFIX.length)
        : shot.selectedVersionId;
      const pinned = versions.find(version => version.id === pinnedId);
      if (!pinned || !isVersionReady(pinned)) return false;
    }
    return true;
  });
}

export function getPreviewableShots(shots: Shot[], shotVersions: Record<string, ShotVersion[]>) {
  return [...shots]
    .sort((a, b) => a.index - b.index)
    .filter(shot => {
      const versions = shotVersions[shot.id] || [];
      return versions.some(isVersionReady);
    });
}

export function getSelectedVersionId(
  shotId: string,
  shotVersions: Record<string, ShotVersion[]>,
  selectedVideoVersions: Record<string, string>,
) {
  const versions = shotVersions[shotId] || [];
  const selectedVersionId = selectedVideoVersions[shotId];
  if (selectedVersionId) {
    if (selectedVersionId.startsWith('erased:')) return selectedVersionId.slice(7);
    const selected = versions.find(version => version.id === selectedVersionId && isVersionReady(version));
    if (selected) return selected.id;
  }
  const completed = [...versions].reverse().find(isVersionReady);
  return completed?.id || '';
}

export function getSelectedVideoURL(
  shotId: string,
  shotVersions: Record<string, ShotVersion[]>,
  selectedVideoVersions: Record<string, string>,
  subtitleEraseStatus: Record<string, SubtitleEraseTask>,
) {
  const versions = shotVersions[shotId] || [];
  const selectedVersionId = selectedVideoVersions[shotId];
  if (selectedVersionId) {
    if (selectedVersionId.startsWith('erased:')) {
      const origId = selectedVersionId.slice(7);
      const eraseTask = subtitleEraseStatus[origId];
      if (eraseTask?.status === 'completed' && eraseTask.resultVideoUrl) {
        return eraseTask.resultVideoUrl;
      }
    }
    const selected = versions.find(version => version.id === selectedVersionId && isVersionReady(version));
    if (selected) return selected.videoURL!;
  }
  const completed = [...versions].reverse().find(isVersionReady);
  return completed?.videoURL || '';
}
